(async () => {
  if (!customElements.get('product-media')) {
    const { default: Swiper } = await import('swiper');
    const { Navigation, Pagination, A11y } = await import('swiper/modules');
    const { Fancybox } = await import('@fancyapps/ui');

    class ProductMedia extends HTMLElement {
      constructor() {
        super();
        this.swiper();
        this.fancybox();
      }

      swiper() {
        // Check if Swiper exists
        if (typeof Swiper == 'undefined') {
          return false;
        }

        // Get swiper element
        this.swiperElement = this.querySelector('[data-product-media-swiper]');

        // Set up ARIA carousel semantics
        this.setupCarouselSemantics();

        // Swiper options
        this.swiperOptions = {
          direction: 'horizontal',
          modules: [Navigation, Pagination, A11y],
          a11y: {
            enabled: true,
            prevSlideMessage: this.dataset.prevMessage || 'Previous slide',
            nextSlideMessage: this.dataset.nextMessage || 'Next slide',
            firstSlideMessage: this.dataset.firstMessage || 'This is the first slide',
            lastSlideMessage: this.dataset.lastMessage || 'This is the last slide',
            paginationBulletMessage: this.dataset.paginationMessage || 'Go to slide {{index}}',
          },
        };

        // Check if we have extra options on the HTML
        if (this && this.dataset && this.dataset.options) {
          const jsonOptions = JSON.parse(this.dataset.options);
          if (jsonOptions) {
            this.swiperOptions = {
              ...this.swiperOptions,
              ...jsonOptions,
            };
          }
        }

        // Init swiper
        this.swiperInstance = new Swiper(this.swiperElement, this.swiperOptions);

        // Add slide numbering after initialization
        this.addSlideNumbering();

        // Update slide numbering on slide change
        this.swiperInstance.on('slideChange', () => {
          this.updateSlideNumbering();
        });

        // Rebuid Swiper in design mode
        if (Shopify.designMode && this.swiperInstance) {
          window.addEventListener('shopify:section:load', () => {
            this.swiperInstance.update();
            this.addSlideNumbering();
          });
        }
      }

      setupCarouselSemantics() {
        if (!this.swiperElement) return;

        // Get carousel label from data attribute or use default
        const carouselLabel = this.dataset.carouselLabel || 'Product media gallery';

        // Add ARIA carousel semantics to the container
        this.setAttribute('role', 'region');
        this.setAttribute('aria-roledescription', 'carousel');
        this.setAttribute('aria-label', carouselLabel);

        // Add group role to the swiper wrapper
        const wrapper = this.swiperElement.querySelector('.swiper-wrapper');
        if (wrapper) {
          wrapper.setAttribute('role', 'group');
          wrapper.setAttribute('aria-label', 'Product images');
        }
      }

      addSlideNumbering() {
        if (!this.swiperInstance) return;

        const slides = this.swiperElement.querySelectorAll('.swiper-slide');
        const totalSlides = slides.length;

        slides.forEach((slide, index) => {
          const slideNumber = index + 1;
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute(
            'aria-label',
            `${slideNumber} ${this.dataset.ofLabel || 'of'} ${totalSlides}`,
          );
        });
      }

      updateSlideNumbering() {
        if (!this.swiperInstance) return;

        const slides = this.swiperElement.querySelectorAll('.swiper-slide');
        const totalSlides = slides.length;

        slides.forEach((slide, index) => {
          const slideNumber = index + 1;
          slide.setAttribute(
            'aria-label',
            `${slideNumber} ${this.dataset.ofLabel || 'of'} ${totalSlides}`,
          );
        });
      }

      /*
       * Get the active index
       */
      swiperGetActiveIndex() {
        return this.swiperInstance.activeIndex;
      }

      /*
       * Slide to a certain index
       */
      swiperSlideTo(index) {
        this.swiperInstance.slideTo(index);
      }

      /*
       * Create fancybox
       */
      fancybox() {
        // Check if Fancybox exists
        if (typeof Fancybox == 'undefined') {
          return false;
        }

        Fancybox.bind('[data-fancybox]', {
          animated: false,
          showClass: false,
          mainClass: 'image-zoom',
        });
      }
    }

    window.ProductMedia = ProductMedia;

    customElements.define('product-media', ProductMedia);
  }
})();
